import { sankeyLinkHorizontal } from 'd3-sankey'

import { flowKindColor } from './flowColors'
import type { FlowFocus, FlowHighlight } from './flowFocus'
import type { LaidOutFlowLink } from './flowLayout'

const REST_OPACITY = 0.38
const LIT_OPACITY = 0.62
const DIM_OPACITY = 0.08
const MIN_STROKE = 1
const MIN_HIT_STROKE = 12

const drawPath = sankeyLinkHorizontal()

// Thick links first, so a thin one is never buried under a wide band,
// and the thin one's hit area sits on top.
function thickestFirst(links: LaidOutFlowLink[]) {
  return [...links].sort(
    (first, second) => (second.width ?? 0) - (first.width ?? 0),
  )
}

function linkOpacity(link: LaidOutFlowLink, highlight: FlowHighlight | null) {
  if (!highlight) return REST_OPACITY
  return highlight.links.has(link.index ?? -1) ? LIT_OPACITY : DIM_OPACITY
}

export function FlowLinks({
  links,
  highlight,
  onFocus,
}: {
  links: LaidOutFlowLink[]
  highlight: FlowHighlight | null
  onFocus: (focus: FlowFocus) => void
}) {
  const ordered = thickestFirst(links)
  return (
    <>
      <g fill="none">
        {ordered.map((link) => (
          <path
            key={link.index}
            d={drawPath(link) ?? undefined}
            stroke={flowKindColor(link.kind)}
            strokeOpacity={linkOpacity(link, highlight)}
            strokeWidth={Math.max(MIN_STROKE, link.width ?? 0)}
            className="transition-[stroke-opacity] duration-150"
          />
        ))}
      </g>
      <g fill="none" stroke="transparent">
        {ordered.map((link) => (
          <path
            key={link.index}
            d={drawPath(link) ?? undefined}
            strokeWidth={Math.max(MIN_HIT_STROKE, link.width ?? 0)}
            pointerEvents="stroke"
            className="cursor-pointer"
            onPointerEnter={() =>
              onFocus({ type: 'link', index: link.index ?? -1 })
            }
            onPointerLeave={() => onFocus(null)}
          />
        ))}
      </g>
    </>
  )
}
